import { apiClient } from "@/shared/apiClient";
import { taskSchema, messageSchema, paginatedSchema, sendMessageRequestSchema } from "@/shared/zod";
import type { Task, Message, Paginated, TaskQueryParams, SendMessageRequest } from "./types";

// Helper functions to map external API values to frontend expected values
function mapExternalStatus(status: string): Task["status"] {
  const statusMap: { [key: string]: Task["status"] } = {
    pending: "pending",
    awaiting_photo: "awaiting_photo",
    awaiting_confirmation: "awaiting_confirmation",
    blocked: "blocked",
    escalated: "escalated",
    completed: "completed",
    cancelled: "cancelled",
  };
  return statusMap[status] || "pending";
}

function mapExternalPriority(priority: string): Task["priority"] {
  const priorityMap: { [key: string]: Task["priority"] } = {
    low: "low",
    normal: "medium",
    high: "high",
    urgent: "urgent",
  };
  return priorityMap[priority] || null;
}

function mapToExternalStatus(status: string): string {
  const statusMap: { [key: string]: string } = {
    pending: "pending",
    awaiting_photo: "awaiting_photo",
    awaiting_confirmation: "awaiting_confirmation",
    blocked: "blocked",
    escalated: "escalated",
    completed: "completed",
    cancelled: "cancelled",
  };
  return statusMap[status] || status;
}

function transformExternalTask(task: any): Task {
  console.log(`[transformExternalTask] Starting transformation`, {
    taskId: task.id,
    taskKeys: Object.keys(task),
    hasInstances: !!task.instances,
    hasTaskInstances: !!task.task_instances,
    hasDataInstances: !!task.data?.instances,
    hasDataTaskInstances: !!task.data?.task_instances,
  });
  
  // Transform instances if they exist
  // Check multiple possible locations for instances
  let instances = task.instances;
  
  // Check alternative field names
  if (!instances && task.task_instances) {
    console.log(`[transformExternalTask] Found instances in task.task_instances`);
    instances = task.task_instances;
  }
  
  // If instances is not directly on task, check nested locations
  if (!instances && task.data?.instances) {
    console.log(`[transformExternalTask] Found instances in task.data.instances`);
    instances = task.data.instances;
  }
  
  if (!instances && task.data?.task_instances) {
    console.log(`[transformExternalTask] Found instances in task.data.task_instances`);
    instances = task.data.task_instances;
  }
  
  console.log(`[transformExternalTask] Instances found:`, {
    found: !!instances,
    isArray: Array.isArray(instances),
    length: instances?.length || 0,
    rawInstances: instances,
  });
  
  const transformedInstances = instances?.map((instance: any) => {
    console.log(`[transformExternalTask] Transforming instance:`, {
      id: instance.id,
      status: instance.status,
      due_date: instance.due_date,
      allKeys: Object.keys(instance),
    });
    return {
      id: instance.id,
      task_id: instance.task_id,
      organization_id: instance.organization_id,
      status: instance.status,
      due_date: instance.due_date,
      original_due_date: instance.original_due_date || null,
      completed_at: instance.completed_at || null,
      cancelled_at: instance.cancelled_at || null,
      escalated_at: instance.escalated_at || null,
      provided_relevant_photo: instance.provided_relevant_photo ?? null,
      photo_id: instance.photo_id || null,
      last_viewed_at: instance.last_viewed_at || null,
      created_at: instance.created_at,
    };
  }) || undefined;
  
  console.log(`[transformExternalTask] Transformed instances:`, {
    count: transformedInstances?.length || 0,
    instances: transformedInstances,
  });

  return {
    id: task.id,
    title: task.title || "Untitled Task",
    description: task.description || null,
    status: mapExternalStatus(task.status || "pending"),
    priority: mapExternalPriority(task.priority),
    assignee: task.owner_employee_id
      ? {
          id: task.owner_employee_id,
          name: "Assigned User",
        }
      : null,
    dueAt: task.next_due || null,
    photoRequired: task.require_photo || false,
    reschedulable: task.reschedulable === true || task.reschedulable === "true" || task.reschedulable === 1,
    reschedule_days: task.reschedule_days ?? null,
    createdAt: task.created_at || new Date().toISOString(),
    updatedAt: task.last_completed_at || task.created_at || new Date().toISOString(),
    messageCount: task.messageCount || 0,
    lastViewedAt: task.last_viewed_at || null,
    instances: transformedInstances,
  };
}

function transformExternalMessage(message: any): Message {
  // Try multiple field names for photo URL (backend might use different names)
  const photoUrl = message.media_url || message.photo_url || message.photoUrl || message.mediaUrl || null;
  
  // Debug logging to see what we're receiving
  if (photoUrl) {
  }
  
  return {
    id: message.id,
    taskId: message.task_id || message.taskId,
    createdAt: message.created_at || message.createdAt,
    direction: message.direction,
    senderName: message.direction === "incoming" ? "You" : "System",
    body: message.content || message.text || message.body || "",
    photoUrl,
  };
}

export const tasksApi = {
  async getTasks(params: TaskQueryParams = {}): Promise<Paginated<Task>> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.status) searchParams.set("status", mapToExternalStatus(params.status));
      if (params.q) searchParams.set("q", params.q);
      if (params.sort) searchParams.set("sort", params.sort);
      if (params.cursor) searchParams.set("cursor", params.cursor);
      
      const queryString = searchParams.toString();
      const endpoint = `/api/employee/tasks${queryString ? `?${queryString}` : ""}`;
      
      const response = await apiClient.get(endpoint);
      
      // Handle new response structure: { success: true, data: [...], count: ... }
      // or legacy structure: { data: [...], nextCursor: ... }
      // If response has 'success' field, it's the new format with nested 'data'
      // Otherwise, response.data might be the array directly, or response itself might be the array
      const tasks = Array.isArray(response) 
        ? response 
        : (response.data || []);
      const transformedTasks = tasks.map(transformExternalTask);
      
      const transformedResponse = {
        data: transformedTasks,
        nextCursor: response.nextCursor || null,
      };
      
      return apiClient.validateResponse(transformedResponse, paginatedSchema(taskSchema));
    } catch (error) {
      console.error("Error in getTasks:", error);
      throw error;
    }
  },

  async getTask(id: number, instanceId?: number): Promise<Task> {
    try {
      const searchParams = new URLSearchParams();
      if (instanceId) {
        searchParams.set("task_instance_id", instanceId.toString());
      }
      const queryString = searchParams.toString();
      const endpoint = `/api/employee/tasks/${id}${queryString ? `?${queryString}` : ""}`;
      
      console.log(`[getTask] Fetching task ${id}${instanceId ? ` with instance ${instanceId}` : ""}`, { 
        endpoint,
        instanceId,
        queryString 
      });
      
      const response = await apiClient.get(endpoint);
      
      // Log raw response structure
      console.log(`[getTask] Raw API response for task ${id}:`, {
        responseType: typeof response,
        hasData: !!response.data,
        responseKeys: Object.keys(response),
        dataKeys: response.data ? Object.keys(response.data) : null,
        rawResponse: JSON.stringify(response, null, 2),
      });
      
      // Check for instances in various locations
      const rawData = response.data || response;
      console.log(`[getTask] Checking for instances in response:`, {
        'rawData.instances': rawData.instances,
        'rawData.task_instances': rawData.task_instances,
        'rawData.data?.instances': rawData.data?.instances,
        'rawData.data?.task_instances': rawData.data?.task_instances,
        allKeys: Object.keys(rawData),
      });
      
      const transformedTask = transformExternalTask(rawData);
      
      console.log(`[getTask] Task ${id} transformed`, {
        hasInstances: !!transformedTask.instances,
        instancesCount: transformedTask.instances?.length || 0,
        instances: transformedTask.instances?.map(inst => ({ 
          id: inst.id, 
          status: inst.status,
          due_date: inst.due_date 
        })) || [],
        transformedTaskKeys: Object.keys(transformedTask),
      });
      
      return apiClient.validateResponse(transformedTask, taskSchema);
    } catch (error) {
      console.error("Error in getTask:", error);
      throw error;
    }
  },

  async getTaskStats(): Promise<any> {
    try {
      const response = await apiClient.get("/api/employee/tasks/stats");
      return response;
    } catch (error) {
      console.error("Error in getTaskStats:", error);
      throw error;
    }
  },

  async getTaskMessages(taskId: number, cursor?: string, taskInstanceId?: number): Promise<Paginated<Message>> {
    try {
      const searchParams = new URLSearchParams();
      if (cursor) searchParams.set("cursor", cursor);
      if (taskInstanceId) searchParams.set("task_instance_id", taskInstanceId.toString());
      
      const queryString = searchParams.toString();
      const endpoint = `/api/employee/tasks/${taskId}/messages${queryString ? `?${queryString}` : ""}`;
      
      const response = await apiClient.get(endpoint);
      
      // Transform external API response to match our expected format
      const messages = response.data || [];
      const transformedMessages = messages.map(transformExternalMessage);
      
      const transformedResponse = {
        data: transformedMessages,
        nextCursor: response.nextCursor || null,
      };
      
      return apiClient.validateResponse(transformedResponse, paginatedSchema(messageSchema));
    } catch (error) {
      console.error("Error in getTaskMessages:", error);
      throw error;
    }
  },

  async sendMessage(taskId: number, data: SendMessageRequest, taskInstanceId?: number): Promise<Message> {
    try {
      // Validate request data
      sendMessageRequestSchema.parse(data);
      
      const payload: any = {
        content: data.body || "", // Can be empty if photoUrl is provided
        message_type: data.photoUrl ? "photo" : "text",
        direction: "incoming",
        status: "pending",
      };

      // Add photoUrl if photo was uploaded
      if (data.photoUrl) {
        payload.photoUrl = data.photoUrl;
      }

      // Add task_instance_id if provided (for recurring task instances)
      if (taskInstanceId) {
        payload.task_instance_id = taskInstanceId;
        console.log("[sendMessage] Adding task_instance_id to payload:", taskInstanceId);
      } else {
        console.warn("[sendMessage] No taskInstanceId provided - message will not be associated with a specific instance");
      }
      
      // Debug logging for message POST request
      const endpoint = `/api/employee/tasks/${taskId}/messages`;
      console.log("[sendMessage] POST Request Debug:", {
        endpoint,
        taskId,
        taskInstanceId: taskInstanceId || null,
        payload: JSON.stringify(payload, null, 2),
        payloadKeys: Object.keys(payload),
        hasTaskInstanceId: 'task_instance_id' in payload,
        taskInstanceIdValue: payload.task_instance_id,
      });
      
      const response = await apiClient.post(endpoint, payload);
      
      console.log("[sendMessage] POST Response:", {
        status: response.status,
        response: response,
      });
      
      // Handle the response structure - the backend returns {success: true, data: {message: "..."}}
      // We need to create a proper message object from the request data since the backend
      // only returns a success message, not the actual message object
      const messageData = {
        id: Date.now(), // Generate a temporary ID
        task_id: taskId,
        created_at: new Date().toISOString(),
        direction: "incoming",
        content: data.body || "", // Use the original message content
        photo_url: data.photoUrl || null, // Include photo URL if present
      };
      
      const transformedMessage = transformExternalMessage(messageData);
      
      return apiClient.validateResponse(transformedMessage, messageSchema);
    } catch (error) {
      console.error("Error in sendMessage:", error);
      throw error;
    }
  },

  async markTaskAsViewed(taskId: number, taskInstanceId?: number, lastViewedAt?: string): Promise<void> {
    try {
      const payload: any = {};
      
      if (taskInstanceId) {
        payload.task_instance_id = taskInstanceId;
      }
      
      if (lastViewedAt) {
        payload.last_viewed_at = lastViewedAt;
      }
      
      await apiClient.put(`/api/employee/tasks/${taskId}/view`, payload);
    } catch (error) {
      console.error("Error in markTaskAsViewed:", error);
      throw error;
    }
  },

  async updateTaskStatus(taskId: number, status: Task["status"], comment?: string, taskInstanceId?: number): Promise<Task> {
    try {
      const payload: any = {
        status: mapToExternalStatus(status),
      };
      
      if (comment) {
        payload.comment = comment;
      }
      
      if (taskInstanceId) {
        payload.task_instance_id = taskInstanceId;
      }
      
      const response = await apiClient.patch(`/api/employee/tasks/${taskId}/status/manual`, payload);
      
      const transformedTask = transformExternalTask(response.data || response);
      return apiClient.validateResponse(transformedTask, taskSchema);
    } catch (error) {
      console.error("Error in updateTaskStatus:", error);
      throw error;
    }
  },

  async verifyPhotos(taskId: number, photos: string[], taskInstanceId?: number): Promise<import("./types").VerifyPhotosResponse> {
    try {
      console.log("verifyPhotos API: Calling endpoint with", photos.length, "photo URLs");
      console.log("verifyPhotos API: Photo URLs:", photos);
      console.log("verifyPhotos API: Task ID:", taskId);
      if (taskInstanceId) {
        console.log("verifyPhotos API: Task Instance ID:", taskInstanceId);
      }
      
      const payload: any = {
        photos,
      };
      
      if (taskInstanceId) {
        payload.task_instance_id = taskInstanceId;
      }
      
      const response = await apiClient.post(`/api/employee/tasks/${taskId}/verify-photos`, payload);
      
      console.log("verifyPhotos API: Received response:", response);
      return response;
    } catch (error) {
      console.error("Error in verifyPhotos API:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
      });
      throw error;
    }
  },

  async rescheduleTaskInstance(taskId: number, instanceId: number, dueDate: string, rescheduleReason: string): Promise<Task> {
    try {
      const payload = {
        due_date: dueDate,
        reschedule_reason: rescheduleReason,
      };
      
      const response = await apiClient.patch(`/api/employee/tasks/${taskId}/instances/${instanceId}/reschedule`, payload);
      
      const transformedTask = transformExternalTask(response.data || response);
      return apiClient.validateResponse(transformedTask, taskSchema);
    } catch (error) {
      console.error("Error in rescheduleTaskInstance:", error);
      throw error;
    }
  },
};
